import type { ReactNode } from "react";
import { AlertTriangle, Inbox, Search, Ship, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./Button";

export interface EmptyStateProps {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  cta?: { label: ReactNode; onClick?: () => void; variant?: "accent" | "outline" | "secondary" };
  className?: string;
}

export function EmptyState({ icon, title, description, cta, className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center text-center py-12 px-4", className)}>
      <div className="text-foreground/20 [&>svg]:w-14 [&>svg]:h-14" style={{ strokeWidth: 1.25 } as any}>{icon ?? <Inbox strokeWidth={1.25} />}</div>
      <div className="text-base font-normal text-foreground font-body mt-4">{title}</div>
      {description && <div className="text-sm font-light text-foreground/70 font-body mt-2 max-w-[384px]" style={{ lineHeight: 1.7 }}>{description}</div>}
      {cta && (
        <Button variant={cta.variant ?? "outline"} size="sm" className="mt-6" onClick={cta.onClick}>
          {cta.label}
        </Button>
      )}
    </div>
  );
}

export const EmptyPresets = {
  noResults: (lang: "EN" | "PT-BR" = "EN") => ({
    icon: <Search />,
    title: lang === "EN" ? "No results found" : "Nenhum resultado encontrado",
    description: lang === "EN" ? "Try adjusting your filters or search terms." : "Tente ajustar seus filtros ou termos de busca.",
  }),
  noShipments: (lang: "EN" | "PT-BR" = "EN") => ({
    icon: <Ship />,
    title: lang === "EN" ? "No active shipments" : "Nenhum embarque ativo",
    description: lang === "EN" ? "Once a shipment is registered it will appear here." : "Quando um embarque for registrado ele aparecerá aqui.",
  }),
  aiEmpty: (lang: "EN" | "PT-BR" = "EN") => ({
    icon: <Sparkles />,
    title: lang === "EN" ? "Ask LogOS to get started" : "Pergunte ao LogOS para começar",
    description: lang === "EN" ? "Describe what you want to monitor and LogOS will set it up." : "Descreva o que deseja monitorar e o LogOS configurará para você.",
  }),
  error: (lang: "EN" | "PT-BR" = "EN") => ({
    icon: <AlertTriangle />,
    title: lang === "EN" ? "Something went wrong" : "Algo deu errado",
    description: lang === "EN" ? "We couldn't load this content. Please try again." : "Não conseguimos carregar este conteúdo. Tente novamente.",
  }),
};
